import { useMemo } from 'react';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { useOfficialExam } from '@/modules/official-exam/presentation/hooks/use-official-exam';
import { breakdownByArea } from '@/modules/diagnostico/domain/entities/area-breakdown';
import { diagnose } from '@/modules/diagnostico/domain/entities/diagnose';
import { AreaBreakdown } from '@/modules/diagnostico/presentation/components/AreaBreakdown';
import { DiagnosticoReading } from '@/modules/diagnostico/presentation/components/DiagnosticoReading';
import {
  GABARITO_SUBMISSION_NOTE,
  SendGabaritoAction,
  useGabaritoSubmission,
} from '@/modules/gabarito/presentation/components/SendGabaritoAction';
import { useMyGabarito } from '../hooks/use-my-gabarito';
import { EmptyState, TabSkeleton } from './EmptyState';

/**
 * A aba Diagnóstico: a leitura de padrão sobre o **gabarito da prova real** —
 * o "Meu padrão" do `enamed-feedback-lp` — e os **acertos por grande área e
 * subárea (H1/H2)**, que dependem das questões da prova oficial (classificação)
 * além do gabarito oficial. O diagnóstico do ensaio é outro, e mora dentro da
 * aba Ensaio.
 */
export function DiagnosticoTab() {
  const { exam } = useTenant();
  const myGabarito = useMyGabarito();
  const officialExam = useOfficialExam();
  const submission = useGabaritoSubmission();

  const reading = useMemo(
    () =>
      officialExam.answerKey && myGabarito.answers
        ? diagnose(officialExam.answerKey, myGabarito.answers)
        : null,
    [officialExam.answerKey, myGabarito.answers],
  );

  const areas = useMemo(
    () =>
      officialExam.data && officialExam.answerKey && myGabarito.answers
        ? breakdownByArea(
            officialExam.data,
            officialExam.answerKey,
            myGabarito.answers,
          )
        : [],
    [officialExam.data, officialExam.answerKey, myGabarito.answers],
  );

  if (myGabarito.isPending || officialExam.isPending) {
    return <TabSkeleton label="Carregando seu diagnóstico" />;
  }

  if (!myGabarito.answers) {
    return (
      <EmptyState
        title="Diagnóstico"
        note={
          submission === 'open'
            ? 'Envie seu gabarito para ver onde você perdeu ponto na prova.'
            : GABARITO_SUBMISSION_NOTE[submission]
        }
      >
        <SendGabaritoAction variant="cta" local="painel" />
      </EmptyState>
    );
  }

  return (
    <div className="stack-lg">
      {reading ? (
        <DiagnosticoReading
          reading={reading}
          examName={exam.shortName}
          subject="prova"
        />
      ) : (
        <EmptyState
          title="Diagnóstico"
          note="O diagnóstico aparece aqui assim que o gabarito oficial for publicado e as questões tiverem a dificuldade classificada."
        />
      )}
      <AreaBreakdown areas={areas} />
    </div>
  );
}
